// WHAT: Tab component for buzzer control and monitoring with dry-run tracking
// WHY:  Provides interface for testing and controlling buzzer hardware with event monitoring
// DOMAIN: Productivity → TimeTracQ
// DEPENDENCIES: useBuzzerControl, useWorkspaceConfig hooks, Tauri event listeners
// RISKS:
//   - NOTE: Event throttling/debouncing may miss important events
//   - NOTE: Dry mode tracking state may not persist across component remounts
//   - NOTE: Event history may grow large - no cleanup mechanism
//   - NOTE: Connection status may not reflect actual hardware state

import { core } from "@tauri-apps/api";
import { listen, type UnlistenFn } from "@tauri-apps/api/event";
import { type FC, useEffect, useState, useCallback, useRef } from "react";

import { Card, Button } from "@/components/ui";
import { disableBuzzerInterception } from "@/domains/productivity/timetracq/hooks/time_tracking/buzzerInterception";
import { useBuzzerControl } from "@/domains/productivity/timetracq/hooks/time_tracking/useBuzzerControl";
import { useWorkspaceConfig } from "@/domains/productivity/timetracq/hooks/time_tracking/useWorkspaceConfig";

/**
 * Dry mode tracking state interface
 */
interface DryModeTrackingState {
  /** Aspect name */
  aspect: string;
  /** Whether tracking is active */
  isActive: boolean;
  /** Session ID */
  sessionId: string;
  /** Start time (ISO) */
  startTime: string;
  /** Workspace name */
  workspace: string;
}

/**
 * Event history item interface
 */
interface EventHistoryItem {
  /** Optional details */
  details?: string;
  /** Event name */
  event: string;
  /** Timestamp */
  timestamp: string;
}

/**
 * WHAT:
 *   Tab component that provides interface for buzzer control, monitoring,
 *   and dry-run tracking with event history.
 *
 * WHY:
 *   Enables testing and debugging of buzzer hardware integration with
 *   comprehensive event monitoring and dry-run tracking capabilities.
 *
 * BEHAVIOR:
 *   - Displays buzzer connection status
 *   - Shows last event and status information
 *   - Provides event history with throttling
 *   - Supports dry-run tracking mode
 *   - Manages buzzer interception
 *   - Updates status in real-time
 *
 * RISKS:
 *   - NOTE: Event throttling/debouncing may miss important events
 *   - NOTE: Dry mode tracking state may not persist across component remounts
 *   - NOTE: Event history may grow large - no cleanup mechanism
 *   - NOTE: Connection status may not reflect actual hardware state
 *
 * @returns Buzzer control and monitoring tab component
 *
 */
export const BuzzerTab: FC = () => {
  const [hex, setHex] = useState<string>("#00FF88");
  const [busy, setBusy] = useState(false);
  const [connected, setConnected] = useState<boolean>(false);
  const [portsOut, setPortsOut] = useState<string[]>([]);
  const [lastEvent, setLastEvent] = useState<string>("");
  const [eventHistory, setEventHistory] = useState<EventHistoryItem[]>([]);
  const [status, setStatus] = useState<null | {
    press_state?: boolean;
    proximity_state?: boolean;
    last_rotation?: null | number;
    last_heartbeat_seen?: boolean;
    last_heartbeat_time_elapsed_ms?: number;
  }>(null);

  // Dry Mode State
  const [dryModeTrackingState, setDryModeTrackingState] = useState<DryModeTrackingState | null>(
    null,
  );

  // Workspace Config
  const [config] = useWorkspaceConfig();

  // NOTE: Event throttling/deduplication
  const lastHeartbeatTimeReference = useRef<number>(0);
  const lastEventTimeReference = useRef<Record<string, number>>({});
  const HEARTBEAT_THROTTLE_MS = 60_000; // NOTE: 1 minute
  const EVENT_DEBOUNCE_MS = 200; // NOTE: 200ms debounce for press/release/rotation

  /**
   * WHAT:
   *   Adds event to history with throttling and deduplication.
   *
   * WHY:
   *   Prevents event history from being flooded with duplicate events.
   */
  const addEventToHistory = useCallback((event: string, details?: string) => {
    const now = Date.now();

    // NOTE: Filter heartbeat events: only once per minute
    if (event === "heartbeat") {
      if (now - lastHeartbeatTimeReference.current < HEARTBEAT_THROTTLE_MS) {
        return;
      }
      lastHeartbeatTimeReference.current = now;
      details = "Heartbeat (throttled: 1/min)";
    }

    // Debounce für press/release/rotation: Nur einmal alle 200ms
    if (["press", "release", "rotation_right", "rotation_left"].includes(event)) {
      const lastTime = lastEventTimeReference.current[event] ?? 0;
      if (now - lastTime < EVENT_DEBOUNCE_MS) {
        return; // Zu schnell, ignorieren
      }
      lastEventTimeReference.current[event] = now;
    }

    const timestamp = new Date().toLocaleTimeString();
    const eventItem: EventHistoryItem = { details: details ?? "", event, timestamp };
    setEventHistory((previous) => {
      const newHistory = [eventItem, ...previous].slice(0, 10); // Max 10 Events
      return newHistory;
    });
    const detailsText = details ? ` (${details})` : "";
    setLastEvent(`${timestamp} • ${event}${detailsText}`);
  }, []);

  // Mock startTracking für Dry Mode
  const dryModeStartTracking = useCallback(
    (workspace: string, aspect: string, color?: string) => {
      const sessionId = crypto.randomUUID();
      const trackingState: DryModeTrackingState = {
        aspect,
        isActive: true,
        sessionId,
        startTime: new Date().toISOString(),
        workspace,
      };
      setDryModeTrackingState(trackingState);
      addEventToHistory("tracking_started", `${workspace}/${aspect}`);

      // Set buzzer color if provided
      if (color) {
        core.invoke("set_buzzer_color", { color }).catch(() => {
          // NOTE: Failed to set buzzer color - silently ignore
        });
      }
    },
    [addEventToHistory],
  );

  // Mock stopTracking für Dry Mode
  const dryModeStopTracking = useCallback(
    (notes?: string) => {
      if (dryModeTrackingState) {
        const duration = Math.floor(
          (Date.now() - new Date(dryModeTrackingState.startTime).getTime()) / 1000,
        );
        const notesText = notes ? `, Notes: ${notes}` : "";
        addEventToHistory("tracking_stopped", `Duration: ${String(duration)}s${notesText}`);
        setDryModeTrackingState(null);

        // Reset buzzer color
        core.invoke("reset_buzzer_color").catch(() => {
          // NOTE: Failed to reset buzzer color - silently ignore
        });
      }
    },
    [dryModeTrackingState, addEventToHistory],
  );

  // useBuzzerControl mit Mock-Funktionen
  const {
    confirmedWorkspace,
    currentOptions,
    currentSelectionIndex,
    lastHeartbeat,
    mode,
    proximityState,
  } = useBuzzerControl({
    config,
    isCompletionDialogOpen: false,
    isTracking: dryModeTrackingState?.isActive ?? false,
    startTracking: dryModeStartTracking,
    stopTracking: dryModeStopTracking,
  });

  // Poll Status
  useEffect(() => {
    let mounted = true;
    const tick = async () => {
      try {
        const s = await core.invoke<{
          press_state?: boolean;
          proximity_state?: boolean;
          last_rotation?: null | number;
          last_heartbeat_seen?: boolean;
          last_heartbeat_time_elapsed_ms?: number;
        }>("get_buzzer_status");
        if (mounted) {
          setStatus(s);
        }
      } catch {
        // NOTE: Failed to get buzzer status - silently ignore to prevent UI disruption
      }
    };
    void tick();
    const id = setInterval(() => {
      void tick();
    }, 1000);
    return () => {
      mounted = false;
      clearInterval(id);
    };
  }, []);

  // Live-Events vom Buzzer anzeigen (nur für History, nicht für Logik)
  // Die Logik wird von useBuzzerControl verarbeitet
  // Wir verwenden useRef, um die aktuellen Werte zu erfassen
  const modeReference = useRef(mode);
  const currentOptionsReference = useRef(currentOptions);
  const currentSelectionIndexReference = useRef(currentSelectionIndex);
  const dryModeTrackingStateReference = useRef(dryModeTrackingState);

  // Aktualisiere Refs wenn State sich ändert
  useEffect(() => {
    modeReference.current = mode;
  }, [mode]);
  useEffect(() => {
    currentOptionsReference.current = currentOptions;
  }, [currentOptions]);
  useEffect(() => {
    currentSelectionIndexReference.current = currentSelectionIndex;
  }, [currentSelectionIndex]);
  useEffect(() => {
    dryModeTrackingStateReference.current = dryModeTrackingState;
  }, [dryModeTrackingState]);

  // Handle press event with delay
  const handlePressEvent = useCallback(
    (eventType: string) => {
      setTimeout(() => {
        const currentMode = modeReference.current;
        const currentOptions_ = currentOptionsReference.current;
        const currentIndex = currentSelectionIndexReference.current;
        const trackingState = dryModeTrackingStateReference.current;

        if (
          currentMode === "workspace" &&
          currentOptions_.length > 0 &&
          currentIndex < currentOptions_.length
        ) {
          const option = currentOptions_[currentIndex];
          const optionText = option ?? "unknown";
          addEventToHistory(eventType, `Selecting workspace: ${optionText}`);
        } else if (
          currentMode === "aspect" &&
          currentOptions_.length > 0 &&
          currentIndex < currentOptions_.length
        ) {
          const option = currentOptions_[currentIndex];
          const optionText = option ?? "unknown";
          addEventToHistory(eventType, `Selecting aspect: ${optionText}`);
        } else if (trackingState?.isActive) {
          addEventToHistory(eventType, "Stopping tracking");
        } else {
          addEventToHistory(eventType);
        }
      }, 100);
    },
    [addEventToHistory],
  );

  // Handle rotation event with delay
  const handleRotationEvent = useCallback(
    (eventType: string) => {
      setTimeout(() => {
        const currentOptions_ = currentOptionsReference.current;
        const currentIndex = currentSelectionIndexReference.current;
        if (currentOptions_.length > 0 && currentIndex < currentOptions_.length) {
          const option = currentOptions_[currentIndex];
          const optionText = option ?? "unknown";
          addEventToHistory(eventType, `Selection: ${optionText}`);
        } else {
          addEventToHistory(eventType);
        }
      }, 100);
    },
    [addEventToHistory],
  );

  useEffect(() => {
    let unlisten: null | UnlistenFn = null;
    void (async () => {
      try {
        unlisten = await listen("buzzer-event", (e) => {
          const event_ = e.payload as null | {
            event_type?: string;
            value?: unknown;
          };
          const eventType = typeof event_?.event_type === "string" ? event_.event_type : "unknown";
          const value = event_?.value === undefined ? undefined : event_.value;

          // Erweitere Event-Details basierend auf Event-Type
          // Verwende Refs für aktuelle Werte (werden nach useBuzzerControl-Verarbeitung aktualisiert)
          let details: string | undefined;
          if (eventType === "press") {
            handlePressEvent(eventType);
            return;
          } else if (eventType === "rotation_right" || eventType === "rotation_left") {
            handleRotationEvent(eventType);
            return;
          } else if (value !== undefined && value !== null) {
            const valueString =
              typeof value === "string" || typeof value === "number" || typeof value === "boolean"
                ? String(value)
                : JSON.stringify(value);
            details = `Value: ${valueString}`;
          }

          addEventToHistory(eventType, details);
        });
      } catch {
        // NOTE: Failed to listen to buzzer events - silently ignore to prevent UI disruption
      }
    })();
    return () => {
      if (unlisten) {
        unlisten();
      }
    };
  }, [addEventToHistory, handlePressEvent, handleRotationEvent]);

  const refreshPorts = async () => {
    try {
      const list = await core.invoke<string[]>("list_midi_ports");
      setPortsOut(list);
    } catch {
      // NOTE: Failed to refresh MIDI ports - silently ignore to prevent UI disruption
    }
  };

  const connect = async () => {
    setBusy(true);
    try {
      await core.invoke("start_buzzer");
      setConnected(true);
      await refreshPorts();
      // WICHTIG: buzzerInterception NICHT aktivieren, da wir die Events verarbeiten wollen
      // enableBuzzerInterception();
    } finally {
      setBusy(false);
    }
  };

  const applyColor = async () => {
    setBusy(true);
    try {
      // ensure connection
      if (!connected) {
        await core.invoke("start_buzzer");
        setConnected(true);
      }
      await core.invoke("set_buzzer_color", { color: hex });
    } finally {
      setBusy(false);
    }
  };

  const resetColor = async () => {
    setBusy(true);
    try {
      await core.invoke("reset_buzzer_color");
    } finally {
      setBusy(false);
    }
  };

  // WICHTIG: buzzerInterception deaktivieren im BuzzerTab
  // Wir wollen die Events verarbeiten, nicht blockieren
  useEffect(() => {
    // Stelle sicher, dass buzzerInterception deaktiviert ist
    disableBuzzerInterception();
    return () => {
      // Beim Verlassen des Tabs auch deaktivieren
      disableBuzzerInterception();
    };
  }, []);

  // Helper: Get mode display text
  const getModeDisplayText = (): string => {
    switch (mode) {
      case "workspace": {
        return "Workspace Selection";
      }
      case "aspect": {
        return "Aspect Selection";
      }
      default: {
        return "Unknown";
      }
    }
  };

  // Helper: Get selection display text
  const getSelectionDisplayText = (): string => {
    if (currentOptions.length > 0 && currentSelectionIndex < currentOptions.length) {
      return currentOptions[currentSelectionIndex] ?? "None";
    }
    return confirmedWorkspace ?? "None";
  };

  return (
    <Card
      className="bg-slate-700/30 border-slate-600/50 shadow-tracq-card"
      padding="lg"
      variant="default"
    >
      <div className="space-y-6">
        <div>
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-semibold text-white">Buzzer Tester</h2>
            <span className="px-2 py-1 text-xs font-medium bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 rounded">
              Dry Mode
            </span>
          </div>
          <p className="text-slate-400">
            Simuliert Tracking-Workflow ohne Supabase-Calls. Sende Farben unabhängig vom Tracking an
            den Buzzer.
          </p>
        </div>

        <Card className="bg-slate-800/30 border-slate-600/50" padding="md" variant="default">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="space-y-1">
              <div className="text-slate-300 text-sm">Verbindung</div>
              <div className="text-white text-lg">
                {connected ? "Verbunden" : "Nicht verbunden"}
              </div>
              {status ? (
                <div className="text-slate-400 text-sm">
                  Press: {status.press_state ? "true" : "false"} • Proximity:{" "}
                  {status.proximity_state ? "true" : "false"} • Heartbeat:{" "}
                  {status.last_heartbeat_seen ? "ok" : "n/a"}
                </div>
              ) : null}
              {!!lastEvent && <div className="text-slate-500 text-xs">Last event: {lastEvent}</div>}
            </div>
            <div className="flex gap-2">
              <Button
                disabled={busy}
                onClick={() => {
                  void connect();
                }}
                variant="primary"
              >
                Verbinden
              </Button>
              <Button
                disabled={busy}
                onClick={() => {
                  void refreshPorts();
                }}
                variant="secondary"
              >
                Ports aktualisieren
              </Button>
            </div>
          </div>
          {portsOut.length > 0 && (
            <div className="mt-3 text-slate-300 text-sm">OUT Ports: {portsOut.join(", ")}</div>
          )}
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
          <div>
            <label className="block text-slate-300 text-sm mb-2">Hex Color</label>
            <input
              className="w-full bg-slate-700/50 border border-slate-600/50 rounded-xl text-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-tracq-bright-blue/30"
              onChange={(e) => {
                setHex(e.target.value);
              }}
              placeholder="#RRGGBB"
              type="text"
              value={hex}
            />
          </div>
          <div className="flex gap-2">
            <Button
              className="flex-1"
              disabled={busy}
              onClick={() => {
                void applyColor();
              }}
              variant="primary"
            >
              Apply
            </Button>
            <Button
              className="flex-1"
              disabled={busy}
              onClick={() => {
                void resetColor();
              }}
              variant="secondary"
            >
              Reset
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-6 gap-3">
          {[
            "#FF0000",
            "#00FF00",
            "#0000FF",
            "#FFFF00",
            "#FF00FF",
            "#00FFFF",
            "#FFFFFF",
            "#000000",
            "#FF8800",
            "#00FF88",
            "#8888FF",
            "#AA00FF",
          ].map((c) => (
            <button
              className="h-10 rounded-lg border border-slate-600/50 hover:border-slate-400 transition"
              key={c}
              onClick={() => {
                setHex(c);
              }}
              style={{ backgroundColor: c }}
            />
          ))}
        </div>

        {/* Workflow Status Card */}
        <Card className="bg-slate-800/30 border-slate-600/50" padding="md" variant="default">
          <div className="space-y-3">
            <div className="text-slate-300 text-sm font-medium">Dry Mode Workflow</div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <div className="text-slate-400 text-xs mb-1">Mode</div>
                <div className="text-white text-sm font-medium capitalize">
                  {getModeDisplayText()}
                </div>
              </div>

              <div>
                <div className="text-slate-400 text-xs mb-1">Selection</div>
                <div className="text-white text-sm font-medium">{getSelectionDisplayText()}</div>
              </div>

              <div>
                <div className="text-slate-400 text-xs mb-1">Tracking</div>
                <div className="text-white text-sm font-medium">
                  {dryModeTrackingState?.isActive ? (
                    <span className="text-green-400">
                      Active ({dryModeTrackingState.workspace}/{dryModeTrackingState.aspect})
                    </span>
                  ) : (
                    <span className="text-slate-500">Idle</span>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-slate-600/50">
              <div>
                <div className="text-slate-400 text-xs mb-1">Proximity</div>
                <div className="text-sm text-slate-200">
                  {proximityState ? "Near" : "Far / Inactive"}
                </div>
              </div>
              <div>
                <div className="text-slate-400 text-xs mb-1">Last Heartbeat</div>
                <div className="text-sm text-slate-200">
                  {lastHeartbeat ? new Date(lastHeartbeat).toLocaleTimeString() : "n/a"}
                </div>
              </div>
            </div>

            {dryModeTrackingState?.isActive ? (
              <div className="pt-2 border-t border-slate-600/50">
                <div className="text-slate-400 text-xs">
                  Started: {new Date(dryModeTrackingState.startTime).toLocaleTimeString()} •
                  Duration:{" "}
                  {Math.floor(
                    (Date.now() - new Date(dryModeTrackingState.startTime).getTime()) / 1000,
                  )}
                  s
                </div>
              </div>
            ) : null}
          </div>
        </Card>

        {/* Event History */}
        <Card className="bg-slate-800/30 border-slate-600/50" padding="md" variant="default">
          <div className="space-y-2">
            <div className="text-slate-300 text-sm font-medium">Event History</div>
            {eventHistory.length > 0 ? (
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {eventHistory.map((item) => {
                  const key = `${item.timestamp}-${item.event}-${item.details ?? ""}`;
                  return (
                    <div className="text-slate-400 text-xs flex items-start gap-2" key={key}>
                      <span className="text-slate-500 font-mono">{item.timestamp}</span>
                      <span className="text-slate-300">{item.event}</span>
                      {item.details ? (
                        <span className="text-slate-500">• {item.details}</span>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-slate-500 text-xs">No events yet</div>
            )}
          </div>
        </Card>
      </div>
    </Card>
  );
};
